import React from "react";
import { motion } from "framer-motion";
import { Quote } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { Reveal, Kicker } from "./Reveal";

const STORY = {
  fr: {
    lead: "Née dans la région du Fouta Djaloo, en Guinée — terre de montagnes, de sources et de traditions peules séculaires — Madame Nènè Gallé Barry a grandi bercée par les parfums et les saveurs qui font la richesse de la cuisine foutanienne.",
    blocks: [
      {
        h: "Une femme du Fouta, une ambition parisienne",
        p: "C'est dans les cuisines familiales de son enfance, auprès de sa mère et de sa grand-mère, qu'elle a appris les gestes, les dosages et les secrets d'une gastronomie transmise de génération en génération. Arrivée en France, elle porte en elle un attachement profond à ses racines et à l'identité peule du Fouta, et constate qu'il manque à Paris un lieu qui rende justice à cette cuisine authentique, encore trop méconnue.",
      },
      {
        h: "La naissance du Goût du Fouta",
        p: "Portée par cette conviction, elle fonde Le Goût du Fouta, pensé comme un pont entre deux mondes : le Fouta Djaloo et la capitale française. Chaque plat est une invitation au voyage — des recettes fidèles aux traditions, préparées avec des ingrédients choisis avec soin, dans le respect des méthodes ancestrales, mais servies dans un cadre chaleureux et convivial.",
      },
      {
        h: "Une cuisine qui célèbre toute l'Afrique de l'Ouest",
        p: "Si la maison puise avant tout dans les traditions peules et foutaniennes, la carte s'ouvre largement sur les grandes saveurs de la région : mafé onctueux à l'arachide, poulet yassa mariné au citron, thiéboudienne emblématique, alloko doré et fonio parfumé — un véritable tour d'horizon gastronomique de l'Afrique de l'Ouest.",
      },
      {
        h: "Une mission qui dépasse la restauration",
        p: "Pour Madame Barry, Le Goût du Fouta est un projet identitaire et culturel : faire rayonner la culture foutanienne et peule, offrir à la diaspora un lieu de retrouvailles au goût de la maison, transmettre un savoir-faire hérité de générations de femmes du Fouta, et créer des ponts entre les cultures par la gastronomie.",
      },
    ],
    closing: "Le Goût du Fouta, c'est l'histoire d'une femme qui a voulu faire voyager les saveurs de son enfance jusqu'aux tables de Paris — et faire du Fouta Djaloo un goût que l'on n'oublie plus.",
  },
  en: {
    lead: "Born in the Fouta Djaloo region of Guinea — a land of mountains, springs and centuries-old Fula traditions — Madame Nènè Gallé Barry grew up surrounded by the aromas and flavours that make Foutanian cooking so rich.",
    blocks: [
      {
        h: "A woman of the Fouta, a Parisian ambition",
        p: "In the family kitchens of her childhood, beside her mother and grandmother, she learned the gestures, the measures and the secrets of a cuisine passed down through generations. Arriving in France with a deep attachment to her Fula roots, she saw that Paris lacked a place that did justice to this authentic, still little-known cuisine.",
      },
      {
        h: "The birth of Le Goût du Fouta",
        p: "Driven by this conviction, she founded Le Goût du Fouta as a bridge between two worlds: the Fouta Djaloo and the French capital. Every dish is an invitation to travel — recipes faithful to tradition, made with carefully chosen ingredients and ancestral methods, served in a warm and welcoming setting.",
      },
      {
        h: "A kitchen that celebrates all West Africa",
        p: "While the house draws first from Fula and Foutanian traditions, the menu opens onto the region's great flavours: silky groundnut mafé, lemon-marinated chicken yassa, iconic thiéboudienne, golden alloko and fragrant fonio — a true culinary tour of West Africa.",
      },
      {
        h: "A mission beyond the table",
        p: "For Madame Barry, Le Goût du Fouta is a cultural project: to celebrate Fula and Foutanian culture, to offer the diaspora a place that tastes of home, to pass on a know-how inherited from generations of Fouta women, and to build bridges between cultures through food.",
      },
    ],
    closing: "Le Goût du Fouta is the story of a woman who wanted to carry the flavours of her childhood all the way to the tables of Paris — and make Fouta Djaloo a taste you never forget.",
  },
};

export default function Founder() {
  const { t, lang } = useLang();
  const s = STORY[lang];

  return (
    <section id="story" className="relative overflow-hidden py-24 md:py-36" data-testid="founder-section">
      <div className="pointer-events-none absolute inset-0 opacity-30 [background:radial-gradient(600px_500px_at_80%_10%,rgba(212,175,55,0.1),transparent)]" />

      <div className="relative mx-auto max-w-7xl px-5 md:px-10">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
          {/* Portrait */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5"
          >
            <div className="relative lg:sticky lg:top-28">
              <div className="relative overflow-hidden rounded-[3px] ring-1 ring-[var(--gold)]/25">
                <img
                  src="/img/founder.jpg"
                  alt="Madame Nènè Gallé Barry, fondatrice"
                  className="aspect-[4/5] w-full object-cover"
                  data-testid="founder-portrait"
                />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
              </div>
              <span className="pointer-events-none absolute -left-3 -top-3 h-16 w-16 border-l border-t border-[var(--gold)]/40" />
              <span className="pointer-events-none absolute -bottom-3 -right-3 h-16 w-16 border-b border-r border-[var(--gold)]/40" />
              <div className="mt-5 text-center lg:text-left">
                <p className="font-serif text-2xl text-[var(--gold-light)]">Nènè Gallé Barry</p>
                <p className="mt-1 font-sans text-[11px] uppercase tracking-[0.24em] text-[var(--text-3)]">
                  {t.founderRole}
                </p>
              </div>
            </div>
          </motion.div>

          {/* Story */}
          <div className="lg:col-span-7">
            <Kicker className="mb-5">{t.founderKicker}</Kicker>
            <h2 className="font-serif text-4xl leading-[0.98] tracking-tight text-[var(--text)] md:text-6xl">
              {t.founderTitle} <span className="italic gold-gradient-text">{t.founderTitleEm}</span>
            </h2>

            <Reveal delay={0.1}>
              <p className="mt-8 border-l-2 border-[var(--gold)]/40 pl-5 font-serif text-xl italic leading-relaxed text-[var(--text)] md:text-2xl">
                {s.lead}
              </p>
            </Reveal>

            <div className="mt-10 space-y-8">
              {s.blocks.map((b, i) => (
                <Reveal key={i} delay={0.05 * i}>
                  <h3 className="font-sans text-[11px] uppercase tracking-[0.24em] text-[var(--gold)]">
                    {b.h}
                  </h3>
                  <p className="mt-3 font-sans text-[15px] leading-relaxed text-[var(--text-2)]">
                    {b.p}
                  </p>
                </Reveal>
              ))}
            </div>

            <Reveal delay={0.1}>
              <div className="mt-10 flex items-start gap-4 border-t border-[var(--gold)]/15 pt-8">
                <Quote className="h-8 w-8 shrink-0 text-[var(--gold)]/50" />
                <p className="font-serif text-xl italic leading-snug text-[var(--gold-light)] md:text-2xl">
                  {s.closing}
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}
